import { Briefcase, FileText, Shield, TrendingUp, CheckCircle, Phone, Users, DollarSign, XCircle, Building, FileCheck, Stamp } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import QuickContact from "@/components/QuickContact";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { RelatedLinks } from "@/components/RelatedLinks";
import AIReadableSection from "@/components/AIReadableSection";
import { businessLawFAQs } from "@/lib/practice-area-faqs";
import HeroSection from "@/components/HeroSection";
import businessHeroImage from "@/assets/business-law-hero.jpg";

const BusinessLaw = () => {
  const services = [
    {
      icon: <Briefcase className="h-8 w-8" />,
      title: "Formation of Business Entities",
      description: "LLC, Corporation, Partnership, and Non-Profit formation services.",
      href: "/practice-areas/business-law/business-formation"
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Partnership Agreements",
      description: "Drafting comprehensive partnership and operating agreements.",
      href: "/practice-areas/business-law/partnership-agreements"
    },
    {
      icon: <Stamp className="h-8 w-8" />,
      title: "Trademark Registration",
      description: "Protect your brand with federal and state trademark registration.",
      href: "/practice-areas/business-law/trademark-registration"
    },
    {
      icon: <TrendingUp className="h-8 w-8" />,
      title: "Purchase and Sale of Business",
      description: "Complete legal support for buying or selling a business.",
      href: "/practice-areas/business-law/purchase-sale-business"
    },
    {
      icon: <FileCheck className="h-8 w-8" />,
      title: "Registered Agent Services",
      description: "Professional registered agent services for your Texas business.",
      href: "/practice-areas/business-law/registered-agent"
    },
    {
      icon: <DollarSign className="h-8 w-8" />,
      title: "Debt Collection",
      description: "Efficient recovery of commercial debts and accounts receivable.",
      href: "/practice-areas/business-law/debt-collection"
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Insurance Claims",
      description: "Representation for business insurance claim disputes.",
      href: "/practice-areas/business-law/insurance-claims"
    },
    {
      icon: <Building className="h-8 w-8" />,
      title: "Formation of Non-Profit",
      description: "501(c)(3) formation and tax-exempt status applications.",
      href: "/practice-areas/business-law/nonprofit-formation"
    },
    {
      icon: <XCircle className="h-8 w-8" />,
      title: "Closing of Business Entities",
      description: "Proper dissolution and winding up of business entities.",
      href: "/practice-areas/business-law/business-closing"
    },
  ];

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section - Optimized for CWV */}
      <HeroSection
        imageSrc={businessHeroImage}
        imageAlt="Business Law Attorney Houston - Formation, Contracts, Transactions"
        height="60vh"
        overlayGradient="bg-gradient-to-r from-primary-dark/95 via-primary/90 to-primary-dark/95"
        priority={true}
      >
        <div className="text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Business Law</h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Legal Solutions to Help Your Business Grow & Thrive
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="cta">
              <Link to="/appointment">Schedule Consultation</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/20">
              <a href="tel:8329303059">
                <Phone className="mr-2 h-5 w-5" />
                Call: (832) 930-3059
              </a>
            </Button>
          </div>
        </div>
      </HeroSection>

      {/* Services Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Business Law Services</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              From startup to established enterprise, we provide comprehensive legal services to protect and grow your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="hover:shadow-xl transition-all border-accent/20 hover:border-accent/50">
                <CardHeader>
                  <div className="text-accent mb-4">{service.icon}</div>
                  <CardTitle>{service.title}</CardTitle>
                  <CardDescription>{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="outline" className="w-full">
                    <Link to={service.href}>Learn More</Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Contact */}
      <QuickContact />

      {/* Business Formation Process */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">Business Formation Process</h2>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {[
                { step: "1", title: "Initial Consultation", desc: "Discuss your business goals and determine the best entity structure" },
                { step: "2", title: "Name Reservation", desc: "Secure your business name with the state" },
                { step: "3", title: "File Formation Documents", desc: "Prepare and file Articles of Organization/Incorporation" },
                { step: "4", title: "Obtain EIN", desc: "Apply for Employer Identification Number with IRS" },
                { step: "5", title: "Operating Agreement", desc: "Draft comprehensive operating agreement or bylaws" },
                { step: "6", title: "Compliance Setup", desc: "Establish recordkeeping, banking, and ongoing compliance" },
              ].map((item) => (
                <div key={item.step} className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-gold-shine text-primary font-bold flex items-center justify-center">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-1">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">Why Businesses Choose Daniel Albert Law</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="text-accent mb-4 flex justify-center">
                <Briefcase className="h-12 w-12" />
              </div>
              <h3 className="text-xl font-bold mb-2">Business-Focused</h3>
              <p className="text-muted-foreground">We understand the unique challenges of running a business</p>
            </div>
            <div className="text-center p-6">
              <div className="text-accent mb-4 flex justify-center">
                <Shield className="h-12 w-12" />
              </div>
              <h3 className="text-xl font-bold mb-2">Proactive Protection</h3>
              <p className="text-muted-foreground">Prevent legal issues before they become problems</p>
            </div>
            <div className="text-center p-6">
              <div className="text-accent mb-4 flex justify-center">
                <TrendingUp className="h-12 w-12" />
              </div>
              <h3 className="text-xl font-bold mb-2">Growth Partner</h3>
              <p className="text-muted-foreground">Strategic legal guidance to support your business growth</p>
            </div>
          </div>
        </div>
      </section>

      <AIReadableSection 
        title={businessLawFAQs.en.title}
        summary={businessLawFAQs.en.summary}
      />

      <RelatedLinks category="business" />
      <CTASection />
      <Footer />
    </div>
  );
};

export default BusinessLaw;
